import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Copy, Download, ExternalLink, Mail, Pencil, Send, Trash2, Presentation } from "lucide-react";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";
import PresentationBuilder from "@/components/PresentationBuilder";
import SendProposalDialog from "@/components/SendProposalDialog";

const shareUrl = (p) => `${window.location.origin}${p.share_path}`;

function NudgeToggle({ p, onChanged }) {
  const api = useApi();
  const toggle = async () => {
    try {
      await api.put(`/presentations/${p.id}/nudge`, { paused: !p.nudge_paused });
      toast.success(p.nudge_paused ? "Daily follow-up reminders turned back on" : "Daily follow-up reminders stopped");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1.5" data-testid={`presentation-nudge-${p.id}`}>
      {p.nudge_paused ? <BellOff size={11} /> : <Bell size={11} />}
      Daily follow-up reminders: {p.nudge_paused ? "Off" : "On"}
      <button type="button" className="underline text-[#5B7FD1] hover:text-white transition-colors ml-1" onClick={toggle} data-testid={`presentation-nudge-toggle-${p.id}`}>
        {p.nudge_paused ? "Turn back on" : "Stop"}
      </button>
    </p>
  );
}

function Row({ p, onEdit, onDelete, onSend, onChanged }) {
  const api = useApi();
  const act = (fn, ok) => async () => {
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const btn = "p-2 text-zinc-400 hover:text-[#5B7FD1] transition-colors";
  return (
    <div className="lux-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`presentation-row-${p.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate">{p.title}{p.client_name ? ` · ${p.client_name}` : ""}</p>
        <p className="text-[11px] text-zinc-500 mt-1">{p.items.length} option{p.items.length === 1 ? "" : "s"} · from {money(p.items[0]?.total_price ?? p.items[0]?.client_price, p.items[0]?.currency || "USD")}{p.all_in ? " all-in" : ""} · {fmtDate(p.updated_at)} · {p.views || 0} view{p.views === 1 ? "" : "s"}</p>
        {p.sent_to && <p className="text-[11px] text-zinc-400 mt-1" data-testid={`presentation-sent-${p.id}`}>Emailed to {p.sent_to} · {fmtDate(p.sent_at)}</p>}
        {p.first_viewed_at && <p className="text-[11px] text-emerald-600 mt-1" data-testid={`presentation-opened-${p.id}`}>Opened by client · {fmtDate(p.first_viewed_at)}</p>}
        {p.first_viewed_at && !p.accepted && <NudgeToggle p={p} onChanged={onChanged} />}
        {p.accepted && (
          <p className="text-[11px] text-[#5B7FD1] mt-1.5" data-testid={`presentation-accepted-${p.id}`}>
            {p.accepted.name} chose Option {p.accepted.item_index + 1} · {p.accepted.aircraft_type} · {money(p.accepted.client_price, p.accepted.currency)}
          </p>
        )}
        {p.items.map((it, i) => (it.unavailable ? `Option ${i + 1} (${it.aircraft_type}) was removed by the flight team` : it.cost_at_send && it.operator_cost !== it.cost_at_send
          ? `Operator cost on Option ${i + 1} changed from ${money(it.cost_at_send, it.currency || "USD")} to ${money(it.operator_cost, it.currency || "USD")}. Review your client price`
          : null)).filter(Boolean).map((msg) => (
          <p key={msg} className="text-[11px] text-amber-300 mt-1.5" data-testid={`presentation-sync-warning-${p.id}`}>{msg}</p>
        ))}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <button className="frame-btn !py-2 !px-3" onClick={() => onSend(p)} data-testid={`presentation-mailto-${p.id}`}><Send size={12} /> Email client</button>
        <button className={btn} title="Copy client link" data-testid={`presentation-copy-${p.id}`} onClick={act(() => navigator.clipboard.writeText(shareUrl(p)), "Client link copied")}><Copy size={15} /></button>
        <a className={btn} title="Open client view" href={`${p.share_path}?preview=1`} target="_blank" rel="noreferrer" data-testid={`presentation-open-${p.id}`}><ExternalLink size={15} /></a>
        <button className={btn} title="Download client PDF" data-testid={`presentation-pdf-${p.id}`} onClick={act(() => downloadBlob(api, `/presentations/${p.id}/pdf`, `Charter-Proposal-${p.client_name || "Client"}.pdf`))}><Download size={15} /></button>
        <button className={btn} title="Email me a copy" data-testid={`presentation-email-me-${p.id}`} onClick={act(async () => { const r = await api.post(`/presentations/${p.id}/email-me`); toast.success(`Sent to ${r.data.sent_to}`); })}><Mail size={15} /></button>
        <button className={btn} title="Edit" data-testid={`presentation-edit-${p.id}`} onClick={() => onEdit(p)}><Pencil size={15} /></button>
        <button className="p-2 text-zinc-500 hover:text-rose-300" title="Delete" data-testid={`presentation-delete-${p.id}`} onClick={() => onDelete(p)}><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export default function PresentationsPanel({ trip, launch }) {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState({ open: false, presentation: null, ids: null });
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (launch) setDialog({ open: true, presentation: null, ids: launch.ids });
  }, [launch]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/trips/${trip.id}/presentations`);
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [api, trip.id]);

  useEffect(() => {
    load();
  }, [load]);

  const del = async (p) => {
    if (!window.confirm("Delete this presentation? The client link will stop working.")) return;
    try {
      await api.delete(`/presentations/${p.id}`);
      toast.success("Presentation deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <section className="mt-14" data-testid="presentations-panel">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-5">
        <div>
          <p className="eyebrow mb-2">For your client</p>
          <h2 className="font-serif-display text-3xl text-white">Client presentations</h2>
          <p className="text-xs text-zinc-500 mt-2 max-w-lg">Pick options, set your marked-up prices, and send a client-ready proposal. Operator and tail details stay hidden.</p>
        </div>
        <button className="frame-btn gold self-start" disabled={!trip.options.length} onClick={() => setDialog({ open: true, presentation: null, ids: null })} data-testid="create-presentation-btn">
          <Presentation size={13} /> Create presentation
        </button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="lux-card px-6 py-8 text-center text-sm text-zinc-500" data-testid="presentations-empty">{trip.options.length ? "No presentations yet." : "Presentations become available once options are added."}</div>}
        {items.map((p) => <Row key={p.id} p={p} onEdit={(x) => setDialog({ open: true, presentation: x, ids: null })} onDelete={del} onSend={setSending} onChanged={load} />)}
      </div>
      <SendProposalDialog p={sending} open={!!sending} onOpenChange={(o) => { if (!o) setSending(null); }} onSent={load} />
      <PresentationBuilder open={dialog.open} presentation={dialog.presentation} initialIds={dialog.ids} trip={trip}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={load} />
    </section>
  );
}
